import { useFrame } from '@react-three/fiber';
import React, { useMemo, useRef } from 'react';
import * as THREE from 'three';

interface RainProps {
  count?: number;
  range?: number;
  height?: number;
  min_speed?: number;
  max_speed?: number;
  color?: string;
}

const Rain: React.FC<RainProps> = ({
  count = 20000,
  range = 2000,
  height = 150,
  min_speed = 3,
  max_speed = 5,
  color = '#6C8094',
}) => {
  const meshRef = useRef<THREE.InstancedMesh | null>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const particles = useMemo(
    () =>
      Array.from({ length: count }, () => ({
        x: (Math.random() < 0.5 ? -1 : 1) * Math.random() * range,
        y: height,
        z: (Math.random() < 0.5 ? -1 : 1) * Math.random() * range,
        speed: Math.random() * (max_speed - min_speed) + min_speed,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [count]
  );

  useFrame(() => {
    if (!meshRef.current) {
      return;
    }

    particles.forEach((particle, index) => {
      const { x, y, z, speed } = particle || {};

      dummy.position.set(x, y, z);
      dummy.position.y -= speed;

      if (dummy.position.y < 0) {
        dummy.position.y = height;
        particle.speed = Math.random() * (max_speed - min_speed) + min_speed;
      }

      particle.y = dummy.position.y;
      dummy.updateMatrix();
      meshRef.current?.setMatrixAt(index, dummy.matrix);
    });

    meshRef.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={meshRef} args={[undefined, undefined, count]}>
      <cylinderGeometry args={[0.1, 0.1, 3, 8]} />
      <meshBasicMaterial color={color} />
    </instancedMesh>
  );
};

export default Rain;
